import React, { useEffect, useMemo, useState, useRef } from "react";
import moment from "moment-timezone";

function pad2(n) {
  return String(n).padStart(2, "0");
}

function formatDurationHHMMSS(totalSeconds) {
  const safe = Number.isFinite(totalSeconds) && totalSeconds > 0 ? Math.floor(totalSeconds) : 0;
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;
  // IMPORTANT: hours should NOT wrap at 24 (moment.utc(...).format("HH") does).
  return `${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}`;
}

function parseSecondsMaybe(value) {
  const n = typeof value === "string" ? Number(value) : value;
  return Number.isFinite(n) ? n : null;
}

function computeTotalSeconds(entries, tz) {
  if (!Array.isArray(entries)) return 0;
  const now = moment().tz(tz);

  return entries.reduce((sum, entry) => {
    const duration = parseSecondsMaybe(entry?.duration);
    if (duration !== null && duration >= 0) return sum + duration;

    // Fallback: compute from start/end timestamps if duration is missing.
    const start = entry?.startTime ? moment(entry.startTime).tz(tz) : null;
    if (!start || !start.isValid()) return sum;

    const end = entry?.endTime ? moment(entry.endTime).tz(tz) : null;
    const effectiveEnd = end && end.isValid() ? end : now; // running entry -> use now

    const diffSeconds = effectiveEnd.diff(start, "seconds");
    return diffSeconds > 0 ? sum + diffSeconds : sum;
  }, 0);
}

// Hook for gradual number animation
const useAnimatedValue = (targetValue, duration = 800, enabled = true) => {
  const [displayValue, setDisplayValue] = useState(targetValue);
  const animationRef = useRef(null);
  const startValueRef = useRef(targetValue);
  const startTimeRef = useRef(null);
  const targetValueRef = useRef(targetValue);
  const isFirstRender = useRef(true);

  useEffect(() => {
    targetValueRef.current = targetValue;

    // On first render or if disabled, set immediately without animation
    if (isFirstRender.current || !enabled) {
      setDisplayValue(targetValue);
      isFirstRender.current = false;
      return;
    }

    if (targetValue === displayValue) return;

    // Cancel any ongoing animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    startValueRef.current = displayValue;
    startTimeRef.current = Date.now();

    const animate = () => {
      const now = Date.now();
      const elapsed = now - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const current = Math.floor(startValueRef.current + (targetValueRef.current - startValueRef.current) * easeOut);

      setDisplayValue(current);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setDisplayValue(targetValueRef.current);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [targetValue, duration, enabled]);

  return displayValue;
};

const TimeTotal = ({ item }) => {
  const tz = global.config.getTimeZone();

  const isTodayGroup = useMemo(() => {
    const groupDate = moment.tz(item?.[0], tz);
    if (!groupDate.isValid()) return false;
    return groupDate.isSame(moment().tz(tz), "day");
  }, [item, tz]);

  const hasRunningEntry = useMemo(() => {
    const entries = item?.[1];
    if (!Array.isArray(entries)) return false;
    return entries.some((e) => e?.startTime && !e?.endTime && (e?.duration == null || e?.duration === 0));
  }, [item]);

  const shouldTick = isTodayGroup && hasRunningEntry;

  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!shouldTick) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [shouldTick]);

  const totalSeconds = useMemo(() => computeTotalSeconds(item?.[1], tz), [item, tz, tick]);

  // Animate the total seconds value
  // Disable animation when ticking (updating every second) to avoid constant animation
  // Enable animation when value changes due to other reasons (e.g., adding/removing entries)
  const animatedSeconds = useAnimatedValue(totalSeconds, 800, !shouldTick);

  return <span>{formatDurationHHMMSS(animatedSeconds)}</span>;
};

export default TimeTotal;